import { DataProxy, FetchResult } from '@apollo/client'
import {
  SelfreflectionQuestionsQuery,
  SelfreflectionQuestionsQueryVariables,
  SelfreflectionQuestion,
  SelfreflectionInput,
  UpdateSelfreflectionQuestionMutationVariables,
  UpdateSelfreflectionQuestionMutation,
  UpdateSelfreflectionQuestionBooleanMutation,
  UpdateSelfreflectionQuestionBooleanMutationVariables,
  SelfreflectionQuestionBoolean,
} from '../../../types'
import { client } from '../../..'
import SelfReflectionMutation from '../../../graphql/self-reflection/SelfReflectionMutations'
import SelfReflectionQueries from '../../../graphql/self-reflection/SelfReflectionQueries'

const updateSelfreflectionQuestionApi = (
  employeeId: number,
  meetingId: number,
  selfreflectionQuestionInput: SelfreflectionInput,
): Promise<FetchResult<UpdateSelfreflectionQuestionMutation>> => {
  return client.mutate<UpdateSelfreflectionQuestionMutation, UpdateSelfreflectionQuestionMutationVariables>({
    mutation: SelfReflectionMutation.UpdateSelfReflection,
    variables: {
      input: selfreflectionQuestionInput,
    },
    update: (store: DataProxy, { data }) => {
      if (!data || !data.updateSelfreflectionQuestion) {
        return
      }

      // update cache
      updateCache(store, data.updateSelfreflectionQuestion, employeeId, meetingId)
    },
  })
}

const updateCache = (store: DataProxy, selfreflectionQuestion: SelfreflectionQuestion, employeeId: number, meetingId: number): void => {
  try {
    const data = store.readQuery<SelfreflectionQuestionsQuery, SelfreflectionQuestionsQueryVariables>({
      query: SelfReflectionQueries.SelfReflectionQuestions,
      variables: { employeeId, meetingId },
    })
    if (!data) return

    const updatedQuestions = data.selfreflectionQuestions.map((el) => (el.id === selfreflectionQuestion.id ? selfreflectionQuestion : el))

    store.writeQuery<{ selfreflectionQuestions: any[] }>({
      query: SelfReflectionQueries.SelfReflectionQuestions,
      variables: { employeeId, meetingId },
      data: {
        selfreflectionQuestions: updatedQuestions,
      },
    })
  } catch (e) {
    throw new Error('Could not update cache for selfreflection question')
  }
}

const updateSelfreflectionQuestionBooleanApi = (
  employeeId: number,
  meetingId: number,
  questionId: number,
  nextSelfreflectionQuestionBoolean: SelfreflectionQuestionBoolean,
): Promise<FetchResult<UpdateSelfreflectionQuestionBooleanMutation>> => {
  const input = {
    id: nextSelfreflectionQuestionBoolean.id,
    value: nextSelfreflectionQuestionBoolean.value,
  }
  return client.mutate<UpdateSelfreflectionQuestionBooleanMutation, UpdateSelfreflectionQuestionBooleanMutationVariables>({
    mutation: SelfReflectionMutation.UpdateSelfReflectionBoolean,
    variables: {
      input: input,
    },
    update: (store: DataProxy, { data }) => {
      if (!data || !data.updateSelfreflectionQuestionBoolean) {
        return
      }

      // update cache
      updateBooleanCache(store, data.updateSelfreflectionQuestionBoolean, employeeId, meetingId, questionId)
    },
  })
}

const updateBooleanCache = (
  store: DataProxy,
  updateSelfreflectionQuestionBoolean: SelfreflectionQuestionBoolean,
  employeeId: number,
  meetingId: number,
  questionId: number,
): void => {
  try {
    const data = store.readQuery<SelfreflectionQuestionsQuery, SelfreflectionQuestionsQueryVariables>({
      query: SelfReflectionQueries.SelfReflectionQuestions,
      variables: { employeeId, meetingId },
    })
    if (!data) return
    const updatedQuestions = data.selfreflectionQuestions.map((question) => {
      if (question.id !== questionId) return question

      const updatedQuestion = {
        ...question,
        subItems: question.subItems.map((subItem) =>
          subItem.id === updateSelfreflectionQuestionBoolean.id ? updateSelfreflectionQuestionBoolean : subItem,
        ),
      }

      return updatedQuestion
    })

    store.writeQuery<SelfreflectionQuestionsQuery, SelfreflectionQuestionsQueryVariables>({
      query: SelfReflectionQueries.SelfReflectionQuestions,
      variables: { employeeId, meetingId },
      data: { selfreflectionQuestions: updatedQuestions },
    })
  } catch (e) {
    throw new Error('Could not update cache for selfreflection boolean question')
  }
}

export default { updateSelfreflectionQuestionApi, updateSelfreflectionQuestionBooleanApi }
